<template>
  <div class="growth-page">
    <section class="growth-hero">
      <div>
        <span class="eyebrow">Growth intelligence</span>
        <h4>Affiliate SEO Advisor</h4>
        <p>Daily analysis of Google visibility, visitor behavior, Amazon clicks, and indexing health. Every recommendation links its diagnosis to observed data.</p>
      </div>
      <div class="hero-actions">
        <span v-if="job" class="schedule-state" :class="{ on: job.enabled }">
          <i></i>{{ job.enabled ? scheduleLabel : 'Manual only' }}
        </span>
        <button class="secondary-btn" type="button" :disabled="loading" @click="fetchOverview">Refresh</button>
        <button class="run-btn" type="button" :disabled="running || Boolean(activeRun)" @click="runAnalysis">
          <span v-if="running || activeRun" class="button-spinner"></span>
          {{ running || activeRun ? 'Analyzing data...' : 'Run analysis now' }}
        </button>
      </div>
    </section>

    <div v-if="error" class="growth-alert">{{ error }}</div>

    <div v-if="loading && !report" class="growth-loading">
      <span class="loading-ring"></span>
      <strong>Loading the latest decision report</strong>
    </div>

    <div v-else-if="!report" class="growth-empty">
      <span class="icon icon-gauge"></span>
      <h5>No analysis report yet</h5>
      <p>Run the advisor once to establish the first 24-hour, 7-day, 30-day, and 90-day baseline.</p>
      <button class="run-btn" type="button" :disabled="running" @click="runAnalysis">Run first analysis</button>
    </div>

    <template v-else>
      <section class="report-banner" :class="healthClass">
        <div class="report-health">
          <span class="health-dot"></span>
          <span>
            <small>Latest assessment</small>
            <strong>{{ healthLabel }}</strong>
          </span>
        </div>
        <p>{{ analysis.executiveSummary }}</p>
        <div class="report-meta">
          <span>Generated {{ fmtDateTime(report.generatedAt) }}</span>
          <span>Google data through {{ report.searchConsoleAsOf || 'unavailable' }}</span>
          <span>{{ report.provider }}/{{ report.model }}</span>
        </div>
      </section>

      <section class="metric-grid" aria-label="Latest thirty day performance">
        <article v-for="metric in headlineMetrics" :key="metric.label" class="metric-card">
          <span>{{ metric.label }}</span>
          <strong>{{ metric.value }}</strong>
          <small :class="metric.tone">{{ metric.detail }}</small>
        </article>
      </section>

      <div class="report-layout">
        <main class="report-main">
          <section class="report-section">
            <header class="section-head">
              <div><span class="eyebrow">Diagnosis</span><h5>What deserves attention</h5></div>
              <span>{{ findings.length }} findings</span>
            </header>
            <div class="finding-list">
              <article v-for="(finding, index) in findings" :key="index" class="finding-card">
                <header>
                  <span class="priority" :class="`priority-${finding.priority}`">{{ finding.priority }}</span>
                  <span class="category">{{ pretty(finding.category) }}</span>
                  <span class="confidence">{{ finding.confidence }} confidence</span>
                </header>
                <h6>{{ finding.title }}</h6>
                <p>{{ finding.diagnosis }}</p>
                <details v-if="finding.evidence && finding.evidence.length">
                  <summary>Evidence · {{ finding.evidence.length }} observations</summary>
                  <ul><li v-for="(item, evidenceIndex) in finding.evidence" :key="evidenceIndex">{{ item }}</li></ul>
                </details>
                <div class="finding-action">
                  <small>Recommended move</small>
                  <p>{{ finding.action }}</p>
                  <span>{{ finding.expectedImpact }}</span>
                </div>
              </article>
            </div>
          </section>

          <section class="report-section">
            <header class="section-head">
              <div><span class="eyebrow">Execution</span><h5>Prioritized action plan</h5></div>
            </header>
            <ol class="action-list">
              <li v-for="action in actions" :key="`${action.order}:${action.action}`">
                <span class="action-order">{{ action.order }}</span>
                <div><span class="owner">{{ action.owner }}</span><strong>{{ action.action }}</strong><p>{{ action.why }}</p><small>Watch: {{ action.metric }}</small></div>
              </li>
            </ol>
          </section>

          <section v-if="experiment" class="report-section experiment-card" :class="{ deferred: !experiment.recommended }">
            <header>
              <div><span class="eyebrow">Controlled experiment</span><h5>{{ experiment.recommended ? experiment.name : 'Experiment deferred' }}</h5></div>
              <span class="experiment-state">{{ experiment.recommended ? `${experiment.durationDays} days` : 'Collect data first' }}</span>
            </header>
            <p class="experiment-hypothesis">{{ experiment.recommended ? experiment.hypothesis : experiment.reason }}</p>
            <template v-if="experiment.recommended">
              <div class="experiment-grid">
                <div><small>Change</small><p>{{ experiment.change }}</p></div>
                <div><small>Primary metric</small><p>{{ experiment.primaryMetric }}</p></div>
                <div><small>Success criteria</small><p>{{ experiment.successCriteria }}</p></div>
                <div><small>Target pages</small><p>{{ (experiment.targetPages || []).join(', ') }}</p></div>
              </div>
              <details v-if="experiment.guardrails && experiment.guardrails.length">
                <summary>Experiment guardrails</summary>
                <ul><li v-for="guardrail in experiment.guardrails" :key="guardrail">{{ guardrail }}</li></ul>
              </details>
            </template>
          </section>
        </main>

        <aside class="report-side">
          <section class="side-card">
            <header><span class="eyebrow">Watch list</span><h5>Metrics for the next run</h5></header>
            <div v-for="metric in watchMetrics" :key="metric.metric" class="watch-row">
              <span class="direction" :class="`direction-${metric.direction}`">{{ directionIcon(metric.direction) }}</span>
              <div><strong>{{ metric.metric }}</strong><small>{{ metric.why }}</small></div>
            </div>
          </section>

          <section v-if="dataQuality.length" class="side-card quality-card">
            <header><span class="eyebrow">Measurement quality</span><h5>Limits to fix or remember</h5></header>
            <div v-for="(issue, index) in dataQuality" :key="index" class="quality-row">
              <strong>{{ issue.issue }}</strong>
              <p>{{ issue.impact }}</p>
              <small>{{ issue.action }}</small>
            </div>
          </section>

          <section class="side-card history-card">
            <header><span class="eyebrow">Report history</span><h5>Previous analyses</h5></header>
            <button
              v-for="item in reports"
              :key="item.id"
              type="button"
              :class="{ active: report.id === item.id }"
              @click="openReport(item.id)"
            >
              <span><strong>{{ fmtShortDate(item.generatedAt) }}</strong><small>{{ item.model }}</small></span>
              <span class="mini-health" :class="`mini-${healthStatus(item)}`">{{ healthStatus(item) }}</span>
            </button>
          </section>

          <router-link class="jobs-link" to="/users/jobs">Open schedule and complete run log &rarr;</router-link>
        </aside>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'

export default {
  name: 'AffiliateSeoAnalytics',
  data () {
    return {
      loading: true,
      running: false,
      error: '',
      report: null,
      reports: [],
      job: null,
      activeRun: null,
      pollTimer: null
    }
  },
  computed: {
    analysis () { return (this.report && this.report.analysis) || {} },
    snapshot () { return (this.report && this.report.snapshot) || {} },
    findings () { return Array.isArray(this.analysis.findings) ? this.analysis.findings : [] },
    actions () { return Array.isArray(this.analysis.nextActions) ? this.analysis.nextActions : [] },
    experiment () { return this.analysis.experiment || null },
    watchMetrics () { return Array.isArray(this.analysis.watchMetrics) ? this.analysis.watchMetrics : [] },
    dataQuality () { return Array.isArray(this.analysis.dataQuality) ? this.analysis.dataQuality : [] },
    healthStatusValue () { return this.analysis.health && this.analysis.health.status || 'watch' },
    healthClass () { return `health-${this.healthStatusValue}` },
    healthLabel () { return this.healthStatusValue === 'good' ? 'Healthy momentum' : this.healthStatusValue === 'critical' ? 'Critical constraints' : 'Needs attention' },
    scheduleLabel () { return this.job && this.job.cron === '15 6 * * *' ? 'Daily schedule active' : 'Automatic' },
    headlineMetrics () {
      const gsc = this.windowData('searchConsole', '30d')
      const audience = this.windowData('audience', '30d')
      const index = this.snapshot.indexHealth || {}
      const gscChange = gsc.changePct || {}
      const audienceCurrent = audience.current || {}
      const rates = audienceCurrent.rates || {}
      const productRate = rates.amazonProductClicksPerProductView != null
        ? rates.amazonProductClicksPerProductView
        : rates.amazonClicksPerProductView
      const indexedShare = Number(index.checkedUrls) ? Number(index.indexedUrls || 0) / Number(index.checkedUrls) : 0
      return [
        { label: 'Google impressions · 30d', value: this.num(gsc.current && gsc.current.impressions), detail: this.changeText(gscChange.impressions, 'vs previous 30d'), tone: this.changeTone(gscChange.impressions) },
        { label: 'Search CTR · 30d', value: this.pct(gsc.current && gsc.current.ctr), detail: this.changeText(gscChange.ctr, 'vs previous 30d'), tone: this.changeTone(gscChange.ctr) },
        { label: 'Amazon product clicks · 30d', value: this.num(audienceCurrent.amazon_product_clicks != null ? audienceCurrent.amazon_product_clicks : audienceCurrent.amazon_clicks), detail: `${this.pct(productRate)} of product views`, tone: productRate > 0 ? 'neutral' : 'bad' },
        { label: 'Indexed URLs', value: `${this.num(index.indexedUrls)} / ${this.num(index.checkedUrls)}`, detail: `${this.pct(indexedShare)} of checked URLs`, tone: indexedShare >= 0.8 ? 'good' : indexedShare >= 0.5 ? 'neutral' : 'bad' }
      ]
    }
  },
  mounted () {
    this.fetchOverview()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    authHeaders () { return { Authorization: `Bearer ${localStorage.usertoken}` } },
    async fetchOverview () {
      if (!this.report) this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(`${apiDomain}admin/affiliate-seo/overview`, { headers: this.authHeaders() })
        this.job = data.job || null
        this.activeRun = data.activeRun || null
        this.reports = data.reports || []
        if (!this.report || (data.latest && Number(data.latest.id) > Number(this.report.id))) this.report = data.latest || this.report
        if (this.activeRun) this.startPolling()
        else {
          this.running = false
          this.stopPolling()
        }
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message || 'Unable to load affiliate SEO reports.'
      } finally {
        this.loading = false
      }
    },
    async openReport (id) {
      this.error = ''
      try {
        const { data } = await axios.get(`${apiDomain}admin/affiliate-seo/reports/${encodeURIComponent(id)}`, { headers: this.authHeaders() })
        this.report = data.report
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || 'Unable to open that report.'
      }
    },
    async runAnalysis () {
      this.running = true
      this.error = ''
      try {
        const { data } = await axios.post(`${apiDomain}admin/affiliate-seo/run`, {}, { headers: this.authHeaders() })
        if (!data.started && data.reason !== 'already-running') throw new Error(data.reason || 'The analysis could not start.')
        this.activeRun = data.run || this.activeRun || { status: 'queued' }
        this.startPolling()
      } catch (error) {
        this.running = false
        this.error = error.response && error.response.data && error.response.data.message || error.message || 'Unable to start the analysis.'
      }
    },
    startPolling () {
      if (this.pollTimer) return
      this.pollTimer = window.setInterval(() => this.fetchOverview(), 4000)
    },
    stopPolling () {
      if (this.pollTimer) window.clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    windowData (area, key) { return this.snapshot[area] && this.snapshot[area][key] || {} },
    healthStatus (report) { return report && report.health && report.health.status || 'watch' },
    directionIcon (direction) { return direction === 'up' ? '↑' : direction === 'down' ? '↓' : direction === 'stable' ? '→' : '?' },
    pretty (value) { return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) },
    num (value) { return Number(value || 0).toLocaleString() },
    pct (value) { return `${(Number(value || 0) * 100).toFixed(Number(value || 0) >= 0.1 ? 1 : 2)}%` },
    changeText (value, suffix) { return value == null ? `New data · ${suffix}` : `${Number(value) >= 0 ? '+' : ''}${Number(value).toFixed(1)}% ${suffix}` },
    changeTone (value) { return value == null ? 'neutral' : Number(value) > 0 ? 'good' : Number(value) < 0 ? 'bad' : 'neutral' },
    fmtDateTime (value) { return value ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'unknown' },
    fmtShortDate (value) { return value ? new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date' }
  }
}
</script>

<style scoped>
.growth-page { width:100%; max-width:1260px; padding:26px 28px 56px; color:#c8d0dc; }
.growth-hero { display:flex; align-items:flex-end; justify-content:space-between; gap:28px; padding-bottom:22px; border-bottom:1px solid #34404f; }
.growth-hero h4 { margin:3px 0 7px; color:#e7edf5; font-size:27px; line-height:1.2; }
.growth-hero p { max-width:750px; margin:0; color:#929fb2; font-size:13px; line-height:1.55; }
.eyebrow { color:#20b7e8; font-size:10px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; }
.hero-actions { display:flex; align-items:center; justify-content:flex-end; gap:9px; flex-wrap:wrap; }
.schedule-state { display:inline-flex; align-items:center; gap:7px; min-height:35px; padding:7px 11px; border:1px solid #34404f; border-radius:7px; color:#8794a8; font-size:10px; font-weight:800; text-transform:uppercase; }
.schedule-state i { width:7px; height:7px; border-radius:50%; background:#687487; }
.schedule-state.on i { background:#20d49c; box-shadow:0 0 9px rgba(32,212,156,.5); }
.run-btn,.secondary-btn { min-height:38px; padding:9px 14px; border-radius:7px; font:inherit; font-size:11px; font-weight:800; text-transform:uppercase; cursor:pointer; }
.run-btn { border:1px solid #14abe0; background:#0b6e91; color:#e6f8ff; }
.run-btn:hover:not(:disabled) { background:#0d84ac; }
.secondary-btn { border:1px solid #445064; background:#0d131c; color:#aeb9ca; }
button:disabled { opacity:.55; cursor:wait; }
.button-spinner,.loading-ring { display:inline-block; width:12px; height:12px; margin-right:7px; border:2px solid rgba(255,255,255,.25); border-top-color:#fff; border-radius:50%; vertical-align:-2px; animation:spin .8s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
.growth-alert { margin:16px 0; padding:12px 14px; border:1px solid #8d3949; border-radius:7px; background:rgba(94,25,38,.22); color:#eca1af; font-size:12px; }
.growth-loading,.growth-empty { display:grid; justify-items:center; gap:9px; margin-top:22px; padding:62px 24px; border:1px solid #34404f; border-radius:10px; background:#0b1018; text-align:center; }
.growth-empty h5 { margin:7px 0 0; color:#e3e9f1; font-size:18px; }.growth-empty p{margin:0 0 10px;color:#8f9bae;font-size:12px}.growth-empty .icon{font-size:28px;color:#1db8e8}
.report-banner { margin-top:20px; padding:19px 21px; border:1px solid #3d495b; border-left-width:3px; border-radius:9px; background:#0c121b; }
.report-banner.health-good{border-left-color:#1ed19b}.report-banner.health-watch{border-left-color:#e4c629}.report-banner.health-critical{border-left-color:#e35d76}
.report-health{display:flex;align-items:center;gap:10px}.health-dot{width:9px;height:9px;border-radius:50%;background:#e4c629}.health-good .health-dot{background:#1ed19b}.health-critical .health-dot{background:#e35d76}.report-health span:last-child{display:grid}.report-health small{color:#738096;font-size:9px;font-weight:800;text-transform:uppercase}.report-health strong{color:#e8edf4;font-size:14px}.report-banner>p{max-width:1060px;margin:13px 0;color:#bdc6d3;font-size:13px;line-height:1.65}.report-meta{display:flex;gap:8px;flex-wrap:wrap}.report-meta span{padding:4px 8px;border-radius:999px;background:#151d29;color:#7e8ba0;font-size:9px;font-weight:700}
.metric-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-top:14px}.metric-card{display:grid;gap:4px;padding:15px;border:1px solid #34404f;border-radius:8px;background:#0b1018}.metric-card>span{color:#79869a;font-size:9px;font-weight:800;text-transform:uppercase}.metric-card strong{color:#edf2f7;font-size:20px}.metric-card small{font-size:10px}.metric-card small.good{color:#29cea0}.metric-card small.bad{color:#e36c80}.metric-card small.neutral{color:#8390a4}
.report-layout{display:grid;grid-template-columns:minmax(0,1fr) 330px;gap:16px;margin-top:16px}.report-main,.report-side{display:grid;align-content:start;gap:16px}.report-section,.side-card{border:1px solid #34404f;border-radius:9px;background:#0a0f17}.report-section{padding:18px}.section-head,.side-card>header,.experiment-card>header{display:flex;align-items:flex-end;justify-content:space-between;gap:14px;padding-bottom:12px;border-bottom:1px solid #2d3745}.section-head h5,.side-card h5,.experiment-card h5{margin:2px 0 0;color:#e4eaf2;font-size:16px}.section-head>span{color:#758196;font-size:10px}
.finding-list{display:grid;gap:10px;margin-top:13px}.finding-card{padding:15px;border:1px solid #2e3947;border-radius:8px;background:#0d131c}.finding-card>header{display:flex;align-items:center;gap:7px}.priority,.category,.confidence{font-size:8px;font-weight:900;letter-spacing:.06em;text-transform:uppercase}.priority{padding:3px 6px;border-radius:4px}.priority-high{background:rgba(226,81,107,.15);color:#ef8294}.priority-medium{background:rgba(231,202,43,.13);color:#e5d56d}.priority-low{background:rgba(34,184,232,.12);color:#72cee9}.category{color:#52bddf}.confidence{margin-left:auto;color:#68758a}.finding-card h6{margin:10px 0 6px;color:#e0e6ef;font-size:14px}.finding-card>p{margin:0;color:#9ba7b9;font-size:11px;line-height:1.55}.finding-card details,.experiment-card details{margin-top:10px}.finding-card summary,.experiment-card summary{color:#7bcce6;font-size:10px;font-weight:700;cursor:pointer}.finding-card ul,.experiment-card ul{margin:8px 0 0;padding-left:18px;color:#8d99ab;font-size:10px;line-height:1.55}.finding-action{margin-top:12px;padding:11px;border-left:2px solid #16b6e8;background:#0a1822}.finding-action small{color:#55c4e5;font-size:8px;font-weight:900;text-transform:uppercase}.finding-action p{margin:4px 0;color:#bec8d6;font-size:11px;line-height:1.5}.finding-action span{color:#79869a;font-size:9px}
.action-list{display:grid;gap:0;margin:13px 0 0;padding:0;list-style:none}.action-list li{display:grid;grid-template-columns:30px minmax(0,1fr);gap:11px;padding:13px 0;border-top:1px solid #27313e}.action-list li:first-child{border-top:0}.action-order{display:grid;width:26px;height:26px;border:1px solid #198bb0;border-radius:7px;color:#74d4f0;font-size:11px;font-weight:900;place-items:center}.action-list li>div{display:grid;gap:4px}.owner{width:max-content;padding:2px 5px;border-radius:3px;background:#142435;color:#64c6e5;font-size:8px;font-weight:900;text-transform:uppercase}.action-list strong{color:#d9e0e9;font-size:11px}.action-list p{margin:0;color:#8592a5;font-size:10px;line-height:1.45}.action-list small{color:#67b8d0;font-size:9px}
.experiment-card{border-color:#36576a;background:linear-gradient(135deg,#0c1b25,#0a1018)}.experiment-card.deferred{border-color:#454a55;background:#0b1018}.experiment-state{padding:5px 8px;border:1px solid #286b83;border-radius:999px;color:#70cde9;font-size:9px;font-weight:800;text-transform:uppercase}.experiment-hypothesis{margin:13px 0;color:#b8c4d2;font-size:12px;line-height:1.55}.experiment-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}.experiment-grid>div{padding:11px;border:1px solid #2e4250;border-radius:7px;background:rgba(8,14,21,.6)}.experiment-grid small{color:#51bddf;font-size:8px;font-weight:900;text-transform:uppercase}.experiment-grid p{margin:5px 0 0;color:#929fb1;font-size:10px;line-height:1.5}
.side-card{padding:15px}.side-card>header{display:block}.watch-row{display:grid;grid-template-columns:25px minmax(0,1fr);gap:9px;padding:11px 0;border-top:1px solid #27313e}.side-card header+.watch-row{border-top:0}.direction{display:grid;width:23px;height:23px;border-radius:50%;background:#182231;color:#95a2b5;font-size:12px;font-weight:900;place-items:center}.direction-up{background:rgba(29,204,151,.13);color:#34d8a8}.direction-down{background:rgba(226,81,107,.14);color:#eb7489}.direction-investigate{background:rgba(232,204,48,.12);color:#ddce67}.watch-row>div{display:grid;gap:3px}.watch-row strong{color:#cbd3de;font-size:10px}.watch-row small{color:#758297;font-size:9px;line-height:1.4}.quality-row{padding:11px 0;border-top:1px solid #27313e}.quality-card header+.quality-row{border-top:0}.quality-row strong{display:block;color:#d1b96a;font-size:10px}.quality-row p{margin:4px 0;color:#7f8c9f;font-size:9px;line-height:1.45}.quality-row small{color:#abb5c4;font-size:9px}.history-card button{display:flex;width:100%;align-items:center;justify-content:space-between;gap:8px;padding:10px 2px;border:0;border-top:1px solid #27313e;background:transparent;color:#9aa6b7;text-align:left;cursor:pointer}.history-card header+button{border-top:0}.history-card button.active{color:#dce5ef}.history-card button>span:first-child{display:grid;gap:2px}.history-card button strong{font-size:10px}.history-card button small{color:#69768a;font-size:8px}.mini-health{padding:3px 6px;border-radius:999px;font-size:7px;font-weight:900;text-transform:uppercase}.mini-good{background:rgba(30,209,155,.13);color:#37d9aa}.mini-watch{background:rgba(228,198,41,.12);color:#dfcf66}.mini-critical{background:rgba(227,93,118,.14);color:#e97d91}.jobs-link{display:block;padding:11px 13px;border:1px solid #2f7892;border-radius:7px;color:#61c8e8;font-size:9px;font-weight:800;text-align:center;text-transform:uppercase}
@media(max-width:1050px){.metric-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.report-layout{grid-template-columns:1fr}.report-side{grid-template-columns:repeat(2,minmax(0,1fr))}.jobs-link{grid-column:1/-1}}
@media(max-width:720px){.growth-page{padding:20px 14px 40px}.growth-hero{display:block}.hero-actions{justify-content:flex-start;margin-top:15px}.metric-grid,.report-side,.experiment-grid{grid-template-columns:1fr}.report-meta{display:grid}.finding-card>header{flex-wrap:wrap}.confidence{margin-left:0}.growth-hero h4{font-size:23px}}
</style>
